<template>
    <div class="live-navigation">
        <Tree
            :value="treeNodes"
            :expandedKeys="expandedKeys"
            @node-select="onNodeSelect"
            @node-expand="onNodeExpand"
            @node-collapse="onNodeCollapse"
            selection-mode="single"
            :selection-keys="selectionKeys"
        />
    </div>
</template>

<script setup lang="ts">
import type { TreeNode } from "primevue/tree";
import Tree from "primevue/tree";
import { computed, onMounted, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";

import { useStekManager } from "@/composables/stek-manager";
import {
    useWorktreeManager,
    useWorktreeStore,
} from "@/composables/worktree-manager";

const route = useRoute();
const router = useRouter();
const { stek } = useStekManager();
const { worktree } = useWorktreeManager();
const worktreeStore = useWorktreeStore();

const expandedKeys = ref<Record<string, boolean>>({});
const selectionKeys = ref<Record<string, boolean>>({});

const sortByName = <T extends { name?: string; identity?: string }>(
    items: T[]
): T[] =>
    [...items].sort((a, b) =>
        (a.name || a.identity || "")
            .toLowerCase()
            .localeCompare((b.name || b.identity || "").toLowerCase())
    );

const hasValidRouteParams = computed(() =>
    Boolean(route.params.orgName && route.params.stekName)
);

const treeNodes = computed<TreeNode[]>(() => {
    if (!stek.value?.identity || !worktree.value || !hasValidRouteParams.value)
        return [];

    const stekName = stek.value.name || route.params.stekName;
    const orgName = route.params.orgName;

    return [
        {
            key: "stek",
            label: stekName || "Stek",
            icon: "pi pi-building",
            data: {
                type: "stek",
                route: "livePage",
                params: { orgName, stekName },
            },
            children: sortByName(worktree.value.domains).map((domain) => ({
                key: `domain-${domain.identity}`,
                label: domain.name || domain.identity,
                icon: "pi pi-cloud",
                data: {
                    type: "domain",
                    route: "liveDomainPage",
                    params: { orgName, stekName, domainId: domain.identity },
                },
                children: sortByName(domain.products).map((product) => ({
                    key: `product-${product.identity}`,
                    label: product.name || product.identity,
                    icon: "pi pi-box",
                    data: {
                        type: "product",
                        route: "liveProductPage",
                        params: {
                            orgName,
                            stekName,
                            productId: product.identity,
                        },
                    },
                    children: sortByName(product.processes).map((process) => ({
                        key: `process-${process.identity}`,
                        label: process.name || process.identity,
                        icon: "pi pi-share-alt",
                        data: {
                            type: "process",
                            route: "liveProcessPage",
                            params: {
                                orgName,
                                stekName,
                                processId: process.identity,
                            },
                        },
                    })),
                })),
            })),
        },
    ];
});

function onNodeSelect(node: TreeNode) {
    if (node.data) {
        router.push({
            name: node.data.route,
            params: node.data.params,
            query: node.data.query || {},
        });
    }
}

function onNodeExpand(node: TreeNode) {
    expandedKeys.value[node.key as string] = true;
}

function onNodeCollapse(node: TreeNode) {
    delete expandedKeys.value[node.key as string];
}

watch(
    [route, worktree],
    () => {
        updateSelection();
    },
    { immediate: true }
);

function updateSelection() {
    if (!worktree.value) return;

    if (route.name === "livePage") {
        selectionKeys.value = { stek: true };
        expandedKeys.value = {};
    } else if (route.name === "liveDomainPage" && route.params.domainId) {
        expandedKeys.value = {
            stek: true,
            [`domain-${route.params.domainId}`]: true,
        };
        selectionKeys.value = { [`domain-${route.params.domainId}`]: true };
    } else if (route.name === "liveProductPage" && route.params.productId) {
        for (const domain of worktree.value.domains) {
            if (
                domain.products.some(
                    (p) => p.identity === route.params.productId
                )
            ) {
                expandedKeys.value = {
                    stek: true,
                    [`domain-${domain.identity}`]: true,
                };
                selectionKeys.value = {
                    [`product-${route.params.productId}`]: true,
                };
                break;
            }
        }
    } else if (route.name === "liveProcessPage" && route.params.processId) {
        for (const domain of worktree.value.domains) {
            for (const product of domain.products) {
                if (
                    product.processes.some(
                        (p) => p.identity === route.params.processId
                    )
                ) {
                    expandedKeys.value = {
                        stek: true,
                        [`domain-${domain.identity}`]: true,
                        [`product-${product.identity}`]: true,
                    };
                    selectionKeys.value = {
                        [`process-${route.params.processId}`]: true,
                    };
                    return;
                }
            }
        }
    }
}

onMounted(() => {
    if (stek.value && !worktree.value) {
        worktreeStore.sync();
    }
    updateSelection();
});
</script>

<style scoped lang="scss">
.live-navigation {
    height: 100%;
    overflow-y: auto;
    padding: 0.5rem 0 0.5rem 0.5rem;
}

:deep(.p-tree) {
    border: none;
    width: 100%;
    padding-right: 0;
}

:deep(.p-tree .p-tree-node-content) {
    padding-right: 0;
}

:deep(.p-tree .p-tree-node-label) {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

:deep(.p-tree .p-tree-node-content.p-highlight) {
    background-color: var(--primary-color);
    color: var(--primary-color-text);
}

:deep(.p-tree .p-tree-node-content:hover) {
    background-color: var(--surface-hover);
}
</style>
